import moment from "moment-timezone"
import { formatDate } from "@core/helpers"

/**
 * Demande de consentement en attente servie par
 * `GET /api/metier/company/billing/source` (bloc `demande`, DEV-621 écran M2).
 * Absente tant que l'API ne la sert pas : les actions n'apparaissent pas.
 */
export type DemandeServie = {
    _id: string
    /** `SIGN` (signature) ou `SHARE` (partage) : choisit la route de suppression */
    type: "SIGN" | "SHARE" | string
    statut: "en_attente" | string
    envoyee_le?: string | null
    relancee_le?: string | null
    /**
     * Ce que deviendrait la place si la demande était supprimée maintenant,
     * jugé par le serveur ; `null` quand la suppression ne libérerait pas la
     * place (rien n'est alors promis).
     */
    si_supprimee?: {
        comptee: boolean
        comptee_jusqu_au: string | null
    } | null
}

/** Code du refus servi quand la demande a déjà été relancée depuis moins de 24 heures */
export const CODE_RELANCE_TROP_TOT = "consentrequest_relance_trop_tot"
/** Code du refus servi quand la demande n'est plus en attente */
export const CODE_DEMANDE_PLUS_EN_ATTENTE = "invalid_consentrequest_state"

/** Fuseau des dates affichées (celui de `formatDate`) */
const FUSEAU = "Europe/Paris"

/**
 * Demande en attente de la ligne, si l'API la sert sous une forme exploitable.
 * @returns la demande, ou `null` (pas de demande, ou API pas encore déployée)
 */
export function demandeEnAttente(source: {
    demande?: DemandeServie | null
}): DemandeServie | null {
    const d = source?.demande
    if (!d || typeof d._id !== "string" || !d._id) return null
    if (d.statut !== "en_attente") return null
    if (d.type !== "SIGN" && d.type !== "SHARE") return null
    return d
}

/** Segment de la route de suppression existante (`/v5/consentrequest/:type/:id`) */
export function typeDeSuppression(demande: DemandeServie): "sign" | "share" {
    return demande.type === "SHARE" ? "share" : "sign"
}

/**
 * Phrase de la boîte de suppression sur la place du forfait (M6, validé) :
 * - facturée jusqu'à la fin de la période : « Elle reste facturée jusqu'au
 *   JJ/MM, fin de la période en cours ; sa place sera libérée le JJ/MM. » ;
 * - sinon : « Sa place sera libérée dès la suppression. » ;
 * - `null` quand le serveur ne promet rien (la place resterait occupée).
 */
export function phrasePlace(demande: DemandeServie): string | null {
    const si = demande.si_supprimee
    if (!si) return null
    if (!si.comptee) return "Sa place sera libérée dès la suppression."
    const fin = formatDate(si.comptee_jusqu_au, "DD/MM", "")
    if (!fin) {
        return "Elle reste facturée jusqu'à la fin de la période en cours ; sa place sera libérée ensuite."
    }
    // Lendemain en jour de calendrier à Paris, pas +24 h : le jour du
    // changement d'heure (25/10/2026) compte 25 heures.
    const lendemain = moment
        .tz(si.comptee_jusqu_au as string, FUSEAU)
        .add(1, "day")
        .format("DD/MM")
    return `Elle reste facturée jusqu'au ${fin}, fin de la période en cours ; sa place sera libérée le ${lendemain}.`
}

/** Réponse de `GET /v5/consentrequest/:id/billing/si-supprimee` (M4) */
export type ReponseSiSupprimeeDemande = {
    si_supprimee: DemandeServie["si_supprimee"]
    periode_fin: string | null
} | null

/**
 * Phrase de la boîte de suppression d'une demande tirée de l'appel ciblé
 * (M4, recette du 15/10), mêmes mots que la liste de facturation
 * ({@link phrasePlace}) ; la fin de période servie complète une date absente.
 * Réponse nulle, bloc nul ou illisible : `null` (boîte comme avant).
 * @param reponse - réponse brute de l'appel ciblé
 */
export function phraseReponseDemande(reponse: unknown): string | null {
    if (!reponse || typeof reponse !== "object") return null
    const r = reponse as { si_supprimee?: unknown; periode_fin?: unknown }
    const si = r.si_supprimee
    if (!si || typeof si !== "object") return null
    const { comptee, comptee_jusqu_au } = si as {
        comptee?: unknown
        comptee_jusqu_au?: unknown
    }
    if (typeof comptee !== "boolean") return null
    const fin =
        typeof comptee_jusqu_au === "string" && comptee_jusqu_au
            ? comptee_jusqu_au
            : typeof r.periode_fin === "string" && r.periode_fin
              ? r.periode_fin
              : null
    return phrasePlace({
        _id: "",
        type: "",
        statut: "",
        si_supprimee: { comptee, comptee_jusqu_au: fin },
    })
}

/**
 * Refus « déjà relancée aujourd'hui », avec l'heure de la prochaine relance
 * possible quand le serveur la sert.
 */
export function messageRelanceTropTot(context: unknown): string {
    const prochaine =
        context && typeof context === "object"
            ? (context as { prochaine_relance_le?: string })
                  .prochaine_relance_le
            : null
    const quand = formatDate(prochaine, "DD/MM [à] HH[h]mm", "")
    return quand
        ? `Cette demande a déjà été relancée aujourd'hui. Nouvelle relance possible le ${quand}.`
        : "Cette demande a déjà été relancée aujourd'hui. Nouvelle relance possible demain."
}
